const SCRIPT_ID = "voiceflow-widget-script";
const SCRIPT_SRC = "https://cdn.voiceflow.com/widget-next/bundle.mjs";
const PROJECT_ID = "696e57fdfe2ebac14a487fcd";
const READY_POLL_MS = 50;
const READY_POLL_MAX = 40;

/** Primary brand color (Voiceflow header). Keep in sync with `public/voiceflow-safe-area.css`. */
const VOICEFLOW_BRAND_COLOR = "#FF9E00";

function voiceflowStylesheetUrl(): string {
	const base = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
	return `${base}/voiceflow-safe-area.css`;
}

async function initVoiceflowWidget(): Promise<void> {
	const vf = window.voiceflow;
	if (!vf?.chat) return;
	await Promise.resolve(vf.chat.load({
		verify: {projectID: PROJECT_ID},
		url: "https://general-runtime.voiceflow.com",
		versionID: "production",
		voice: {
			url: "https://runtime-api.voiceflow.com"
		},
		assistant: {
			color: VOICEFLOW_BRAND_COLOR,
			stylesheet: new URL(voiceflowStylesheetUrl(), window.location.href).href
		}
	}));
}

let closeListenerAdded = false;

function ensureCloseListener(): void {
	if (closeListenerAdded) return;
	closeListenerAdded = true;
	window.addEventListener("message", (e: MessageEvent) => {
		try {
			const data = (typeof e.data === "string") ? JSON.parse(e.data) : e.data;
			if (data?.type && (
				data.type === "voiceflow:close" ||
				data.type === "vf-widget:close" ||
				data.type === "CLOSE"
			)) {
				window.voiceflow?.chat?.hide();
			}
		} catch {
			// ignore malformed postMessage payloads
		}
	});
}

async function waitForChatApi(): Promise<void> {
	for (let i = 0; i < READY_POLL_MAX; i++) {
		if (window.voiceflow?.chat) return;
		await new Promise(r => window.setTimeout(r, READY_POLL_MS));
	}
	throw new Error("Voiceflow chat API unavailable");
}

async function ensureScriptLoaded(): Promise<void> {
	if (!document.getElementById(SCRIPT_ID)) {
		await new Promise<void>((resolve, reject) => {
			const script = document.createElement("script");
			script.id = SCRIPT_ID;
			script.src = SCRIPT_SRC;
			script.type = "text/javascript";
			script.onload = () => resolve();
			script.onerror = () => reject(new Error("Voiceflow script failed to load"));
			document.body.appendChild(script);
		});
	}
	await waitForChatApi();
}

let widgetConfigured = false;
let configurePromise: Promise<void> | null = null;

function ensureWidgetConfigured(): Promise<void> {
	if (widgetConfigured) return Promise.resolve();
	if (!configurePromise) {
		configurePromise = ensureScriptLoaded()
			.then(() => initVoiceflowWidget())
			.then(() => {
				widgetConfigured = true;
			})
			.catch((err) => {
				configurePromise = null;
				throw err;
			});
	}
	return configurePromise;
}

/**
 * Injects the Voiceflow script on first use, initializes the widget, then opens the chat.
 * Avoids loading the widget at app startup (no launcher flash on refresh).
 */
export async function openVoiceflowFrankChat(): Promise<void> {
	ensureCloseListener();
	try {
		await ensureWidgetConfigured();
	} catch {
		return;
	}
	const chat = window.voiceflow?.chat;
	if (!chat) return;
	chat.show();
	chat.open();
}
